import _ from 'lodash';
import { ApolloClient, InMemoryCache, HttpLink, from } from '@apollo/client';

import { onError } from '@apollo/client/link/error';

import { relayStylePagination } from '@apollo/client/utilities';
import { csrfFromDom } from 'common/utils/server';

/*
Unlike all other apollo client behavior, relayStylePagination fields don't automatically trigger
refetching when input variables change.

This means that `data` and `variables` won't necessarily match in situations like
`const { data } = useQuery(MY_QUERY, { variables })`, which is bad.

deterministicPagination to "corrects" this behavior so that we don't have to call
refetch({ variables }) manually to ensure that query results update to match the variables.

If you want to do deep optimization of a query and need fine-grained control of when and how that
query runs, please use use `apolloClient.query` (that's the apolloClient we pass to
<ApolloProvider />) or `useLazyQuery`.
*/
const relayManagedFields = ['first', 'last', 'after', 'before'];
const deterministicPagination = relayStylePagination(args => {
  return Object.keys(_.omit(args || {}, relayManagedFields));
});

const httpLink = new HttpLink({
  uri: '/graphql',
  headers: { 'X-CSRF-token': csrfFromDom() },
  fetch: (uri, options) => {
    const { operationName } = JSON.parse(options.body);
    return fetch(`${uri}?opname=${operationName}`, options);
  },
});

const errorLink = onError(({ graphQLErrors, networkError }) => {
  if (graphQLErrors) {
    // TODO: handle graphQLErrors errors
    graphQLErrors.forEach(({ message, locations, path }) => {
      // eslint-disable-next-line no-console
      console.log(`[GraphQL error]: Message: ${message}, Location: ${locations}, Path: ${path}`);
    });
  }
  // TODO: handle network errors
  // eslint-disable-next-line no-console
  if (networkError) console.log(`[Network error]: ${networkError}`);
});

export function getInMemoryCache(options) {
  return new InMemoryCache({
    possibleTypes: {
      MetricSelections: ['LeaderboardMetrics', 'GeneralMetric'],
    },
    typePolicies: {
      Query: {
        fields: {
          employees: deterministicPagination,
          employee: { merge: true },
          teams: deterministicPagination,
          supervisors: deterministicPagination,
          reportingGroups: deterministicPagination,
          calculateMetricByGroup: deterministicPagination,
          properties: deterministicPagination,
          coacheesData: deterministicPagination,
          interactions: deterministicPagination,
          qaForms: deterministicPagination,
          reportingGroupParticipantsPreview: deterministicPagination,
          dateFilterConfig: { merge: true },
        },
      },
      Team: {
        fields: {
          employees: deterministicPagination,
          children: deterministicPagination,
        },
      },
      ReportingGroup: {
        fields: {
          members: deterministicPagination,
          children: deterministicPagination,
        },
      },
      Employee: {
        fields: {
          coachingSessions: deterministicPagination,
        },
      },
      CoachingSessionActionItemsResponse: {
        fields: {
          collection: deterministicPagination,
        },
      },
      QaAssignmentPeriod: {
        fields: {
          reviewers: deterministicPagination,
          reviewees: deterministicPagination,
        },
      },
      CoacheesData: {
        keyFields: ['entityType', 'entityId'],
      },
      QaCalibration: {
        fields: {
          qaReviews: deterministicPagination,
        },
      },
    },
    ...options,
  });
}

const apolloInstance = new ApolloClient({
  uri: '/graphql',
  cache: getInMemoryCache(),
  link: from([errorLink, httpLink]),
  headers: { 'X-CSRF-token': csrfFromDom() },
});

export default apolloInstance;
